/**
 * SHARED PROTECTED-OPERATION LAYER
 * --------------------------------
 * Every authenticated server function in this app must use the
 * `requireSupabaseAuth` exported HERE, not the generated one.
 *
 * It composes the generated Supabase bearer-token middleware and then enforces
 * the single canonical rule:
 *
 *   A valid verification/login code may establish identity, but a session that
 *   belongs to an account WITHOUT a real user-created password may not perform
 *   any protected operation until that password exists.
 *
 * This is enforced here — at the RPC boundary — precisely so a caller holding a
 * no-password bearer token cannot bypass the UI and call the backend directly.
 *
 * The only functions allowed to run while setup is pending are the password
 * setup operations themselves, which deliberately import the generated
 * middleware directly (see `src/lib/password-setup.functions.ts`).
 */

import { createMiddleware } from "@tanstack/react-start";
import { requireSupabaseAuth as requireSupabaseSession } from "@/integrations/supabase/auth-middleware";
import { PASSWORD_SETUP_REQUIRED } from "@/lib/password-setup";

export class PasswordSetupRequiredError extends Error {
  readonly code = PASSWORD_SETUP_REQUIRED;
  constructor() {
    super(PASSWORD_SETUP_REQUIRED);
    this.name = "PasswordSetupRequiredError";
  }
}

/**
 * Resolves password state for the caller.
 *
 * Readiness is live server-owned database state. Auth user metadata is never
 * consulted because a signed-in user can edit their own user_metadata.
 */
export async function assertPasswordSetForCaller(context: {
  supabase: { rpc: (name: string) => Promise<{ data: unknown; error: unknown }> };
}): Promise<void> {
  const { data, error } = await context.supabase.rpc("current_account_password_ready");
  if (error || data !== true) throw new PasswordSetupRequiredError();
}

export const requireSupabaseAuth = createMiddleware({ type: "function" })
  .middleware([requireSupabaseSession])
  .server(async ({ next, context }) => {
    await assertPasswordSetForCaller(context as never);
    return next();
  });
